import COUNTRIES_EN from 'world_countries_lists/data/countries/en/world.json'

/**
 * Country (ISO alpha-2, lower case — the `world_countries_lists` convention) → IANA time zone.
 * Multi-zone countries get the zone of their capital / main city; the organizer can still pick
 * another one in the time-zone select (mock _kit/create.html: "Set from the country — you can
 * change it."). Every value must exist in `Constants/TimezonesNames.js` (see the vitest next to
 * this file).
 */
export const COUNTRY_TIMEZONES = {
    // Europe
    fr: 'Europe/Paris',
    be: 'Europe/Brussels',
    ch: 'Europe/Zurich',
    lu: 'Europe/Luxembourg',
    mc: 'Europe/Monaco',
    es: 'Europe/Madrid',
    pt: 'Europe/Lisbon',
    it: 'Europe/Rome',
    de: 'Europe/Berlin',
    nl: 'Europe/Amsterdam',
    gb: 'Europe/London',
    ie: 'Europe/Dublin',
    at: 'Europe/Vienna',
    dk: 'Europe/Copenhagen',
    se: 'Europe/Stockholm',
    no: 'Europe/Oslo',
    fi: 'Europe/Helsinki',
    is: 'Atlantic/Reykjavik',
    pl: 'Europe/Warsaw',
    cz: 'Europe/Prague',
    sk: 'Europe/Bratislava',
    hu: 'Europe/Budapest',
    si: 'Europe/Ljubljana',
    hr: 'Europe/Zagreb',
    ba: 'Europe/Sarajevo',
    rs: 'Europe/Belgrade',
    me: 'Europe/Podgorica',
    mk: 'Europe/Skopje',
    al: 'Europe/Tirane',
    gr: 'Europe/Athens',
    bg: 'Europe/Sofia',
    ro: 'Europe/Bucharest',
    md: 'Europe/Chisinau',
    ua: 'Europe/Kiev',
    by: 'Europe/Minsk',
    ru: 'Europe/Moscow',
    lt: 'Europe/Vilnius',
    lv: 'Europe/Riga',
    ee: 'Europe/Tallinn',
    mt: 'Europe/Malta',
    cy: 'Asia/Nicosia',
    ad: 'Europe/Andorra',
    li: 'Europe/Vaduz',
    tr: 'Europe/Istanbul',
    // Africa & Middle East
    ma: 'Africa/Casablanca',
    dz: 'Africa/Algiers',
    tn: 'Africa/Tunis',
    eg: 'Africa/Cairo',
    sn: 'Africa/Dakar',
    ci: 'Africa/Abidjan',
    ng: 'Africa/Lagos',
    gh: 'Africa/Accra',
    cm: 'Africa/Douala',
    ke: 'Africa/Nairobi',
    za: 'Africa/Johannesburg',
    mu: 'Indian/Mauritius',
    mg: 'Indian/Antananarivo',
    ae: 'Asia/Dubai',
    sa: 'Asia/Riyadh',
    qa: 'Asia/Qatar',
    bh: 'Asia/Bahrain',
    kw: 'Asia/Kuwait',
    om: 'Asia/Muscat',
    il: 'Asia/Jerusalem',
    lb: 'Asia/Beirut',
    jo: 'Asia/Amman',
    // Americas
    us: 'America/New_York',
    ca: 'America/Toronto',
    mx: 'America/Mexico_City',
    br: 'America/Sao_Paulo',
    ar: 'America/Argentina/Buenos_Aires',
    cl: 'America/Santiago',
    co: 'America/Bogota',
    pe: 'America/Lima',
    uy: 'America/Montevideo',
    ve: 'America/Caracas',
    ec: 'America/Guayaquil',
    bo: 'America/La_Paz',
    py: 'America/Asuncion',
    pa: 'America/Panama',
    cr: 'America/Costa_Rica',
    gt: 'America/Guatemala',
    do: 'America/Santo_Domingo',
    pr: 'America/Puerto_Rico',
    jm: 'America/Jamaica',
    gp: 'America/Guadeloupe',
    mq: 'America/Martinique',
    // Asia & Oceania
    jp: 'Asia/Tokyo',
    kr: 'Asia/Seoul',
    cn: 'Asia/Shanghai',
    hk: 'Asia/Hong_Kong',
    tw: 'Asia/Taipei',
    sg: 'Asia/Singapore',
    my: 'Asia/Kuala_Lumpur',
    th: 'Asia/Bangkok',
    id: 'Asia/Jakarta',
    ph: 'Asia/Manila',
    vn: 'Asia/Ho_Chi_Minh',
    in: 'Asia/Kolkata',
    pk: 'Asia/Karachi',
    lk: 'Asia/Colombo',
    kz: 'Asia/Almaty',
    au: 'Australia/Sydney',
    nz: 'Pacific/Auckland',
    nc: 'Pacific/Noumea',
    pf: 'Pacific/Tahiti',
}

const ALPHA2_BY_NAME = new Map(COUNTRIES_EN.map((country) => [country.name.toLowerCase(), country.alpha2]))

/**
 * IANA zone for a country given by its English name (the value `CountryInput` emits), or
 * undefined when the country is unknown / not in the map.
 */
export const getTimezoneForCountry = (englishName) => {
    if (!englishName) return undefined
    const alpha2 = ALPHA2_BY_NAME.get(String(englishName).trim().toLowerCase())
    return alpha2 ? COUNTRY_TIMEZONES[alpha2] : undefined
}
